import {z} from 'zod';
import {defineToolRegistryManifest} from '../../types/base-tool-types';
import {dataToolSchemas} from '../../utils/schemas';
import {CMS_RICH_TEXT_WHTML_GRAMMAR} from '../cms-rich-text-whtml-grammar';

const toolName = 'create-cms-collection-items' as const;

const itemFieldDataSchema = z
  .object({
    name: z.string().min(1).describe('The name of the collection item'),
    slug: z
      .string()
      .min(1)
      .describe(
        'The URL slug for the collection item, usually the same as the name but in kebab-case'
      ),
  })
  .passthrough()
  .describe(
    'The field data for a collection item. Must include required fields like name and slug. Additional custom fields should use their field slugs as the property key (which are always in kebab-case), not the free-form field names.'
  );

const argumentZod = z.object({
  collectionId: dataToolSchemas.collectionId,
  items: z
    .array(itemFieldDataSchema)
    .min(1)
    .max(100)
    .describe(
      'Array of field data for items to create. Can create 1-100 items in a single request.'
    ),
  isArchived: z
    .boolean()
    .optional()
    .describe('Whether all items should be archived. Defaults to false.'),
  isDraft: z
    .boolean()
    .optional()
    .describe('Whether all items should be drafts. Defaults to true.'),
  cmsLocaleIds: z
    .array(dataToolSchemas.localeId)
    .optional()
    .describe(
      'Array of locale IDs where the items will be created. If not provided, items will only be created in the primary locale.'
    ),
  skipInvalidFiles: z
    .boolean()
    .optional()
    .describe(
      'Whether to skip invalid files during processing. Defaults to true.'
    ),
});

export const createCMSCollectionItemsManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Create CMS collection items',
  family: 'cms',
  llmTool: {
    name: toolName,
    description:
      'Create one or more items in a CMS collection. This tool allows you to add multiple pieces of content to a collection in a single request, with support for multiple locales. Items will be created in staging by default. Can create 1-100 items per request.\n\n' +
      CMS_RICH_TEXT_WHTML_GRAMMAR,
    inputSchema: argumentZod,
  },
  type: 'server',
  permission: [
    {
      resource: 'SiteCollection',
      action: 'create_items',
      resolveResources: (args) => [
        {type: 'SiteCollection' as const, id: args.collectionId},
      ],
    },
  ],
  confirmBeforeExecuting: false,
});

export type CreateCMSCollectionItemsArgumentType = z.infer<typeof argumentZod>;
